import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import { realtimeSubscribeGoal, realtimeUnsubscribeGoal } from '../../redux/actions/realtime';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid,
  Link, Paper, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  GoalsIcon,
} from '../global/CustomIcons';
import BoltIcon from '@mui/icons-material/Bolt';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import DatasetIcon from '@mui/icons-material/Dataset';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { fetchRequest as fetchDetail, clearState as clearDetail } from '../../redux/actions/discovery/goalDetail';
import { fetchRequest as fetchRun, clearState as clearRun } from '../../redux/actions/discovery/run';
import { fetchRequest as rerunRequest, clearState as clearRerun } from '../../redux/actions/discovery/rerunGoal';
import { fetchRequest as publishRequest, clearState as clearPublish } from '../../redux/actions/discovery/publishSegment';
import { fetchRequest as deleteGoalRequest, clearState as clearDeleteGoal } from '../../redux/actions/discovery/deleteGoal';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import StatusChip from '../common/StatusChip';
import { CMButtonOutlinedSmall, CMButtonError } from '../../containers/styledComponents';
import GoalActivity from './GoalActivity';
import GoalDashboard from './GoalDashboard';
import GoalServing from './GoalServing';
import DiscoveryRunCard from './DiscoveryRunCard';
import RelationshipGraph from './RelationshipGraph';
import TournamentBoard from './TournamentBoard';
import GoalSettingsCard from './GoalSettingsCard';
import ForecastStudioPage from './ForecastStudioPage';
import ScenariosPage from '../scenarios/ScenariosPage';
import AutopilotTimelineCard from './AutopilotTimelineCard';
import GoalAlertsPanel from './GoalAlertsPanel';
import ShockAnaloguesCard from './ShockAnaloguesCard';
import PathwaysPanel from './PathwaysPanel';
import PredictiveInfluencePanel from './PredictiveInfluencePanel';
import FleetGoalDetailPage from './FleetGoalDetailPage';
import { useGoalTour } from './GoalTourGuide';
import { SECTION_LABELS, GoalSideNav, GoalSummaryStrip } from './GoalChrome';
import useBuildTheater from './BuildTheater';
import DriverRevealChart from './DriverRevealChart';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { fetchRequest as fetchActivityRequest, clearState as clearActivityState } from '../../redux/actions/discovery/goalActivity';
import { useDisplayNames } from '../../utils/displayNames';

const GOAL_TYPE_LABELS = {
  forecast_value: 'Forecast value',
  forecast_distribution: 'Forecast distribution',
  trend: 'Trend',
  anomaly: 'Anomaly detection',
};

const GOAL_TYPE_ICONS = {
  forecast_value: ShowChartIcon,
  forecast_distribution: BarChartIcon,
  trend: TrendingUpIcon,
  anomaly: WarningAmberIcon,
};

// ``serving`` = a deployed champion is producing forecasts; ``live`` is a
// legacy alias kept for goals persisted before the rename (renders green).
// This is separate from the goal's data mode (``options.live``).
const STATUS_TONES = { idle: 'neutral', running: 'info', serving: 'success', live: 'success', failed: 'error' };
// Goal lifecycle states where discovery has settled (not running/failed) — the
// driver set is stable enough to copy into a segment.
const SETTLED_STATUSES = ['serving', 'live', 'idle'];

const GoalDetailPage = ({
  detail, detailLoading, fetchGoal, clearGoal,
  run, runClear,
  rerun, rerunState, rerunClear,
  publish, publishState, publishClear,
  deleteGoal, deleteState, deleteClear,
  activity, tournament,
}) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { goalId, section: sectionParam } = useParams();
  const section = SECTION_LABELS[sectionParam] ? sectionParam : 'overview';
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Subscribe to this goal's live channel while the page is open (ref-counted,
  // released on unmount / goal change). Goal events arrive as generic
  // REALTIME_EVENT; map them in the realtime saga as emits are added.
  useEffect(() => {
    if (!goalId) return undefined;
    dispatch(realtimeSubscribeGoal(goalId));
    return () => dispatch(realtimeUnsubscribeGoal(goalId));
  }, [dispatch, goalId]);

  const goal = detail?.goal;
  const totalCost = detail?.total_cost;
  const runDoc = run || detail?.latest_run;
  const runActive = runDoc && ['queued', 'running'].includes(runDoc.status);

  useEffect(() => {
    fetchGoal({ goalId });
    return () => { clearGoal(); runClear(); rerunClear(); publishClear(); deleteClear(); };
  }, [goalId, fetchGoal, clearGoal, runClear, rerunClear, publishClear, deleteClear]);

  // Activity (lifecycle phases + stats) is owned here, not by the Overview
  // card: the guided tour needs it live on EVERY section. Fetch on mount,
  // refetch on lifecycle transitions, clear when leaving the goal.
  useEffect(() => {
    if (goalId) dispatch(fetchActivityRequest({ goalId }));
    return () => { dispatch(clearActivityState()); };
  }, [goalId, dispatch]);
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string' && (
      msg.event.indexOf('goal.') === 0
      || msg.event.indexOf('training.') === 0
      || msg.event.indexOf('deployment.') === 0
    ),
    () => { if (goalId) dispatch(fetchActivityRequest({ goalId })); },
  );

  // Once the delete succeeds the goal no longer exists — leave for the list.
  useEffect(() => {
    if (deleteState?.response) {
      setDeleteOpen(false);
      deleteClear();
      navigate('/goals');
    }
  }, [deleteState, deleteClear, navigate]);

  // No polling: the run doc + goal detail are refreshed live by the realtime
  // saga from this goal's `goal.run.*` WebSocket events (the page subscribes to
  // `goal:<id>` on mount). See core/WEBSOCKETS.md.

  const headerChips = useMemo(() => {
    if (!goal) return null;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <StatusChip status={goal.status} tone={STATUS_TONES[goal.status] || 'neutral'} />
        <StatusChip
          status=""
          tone={(goal?.options?.live) !== false ? 'info' : 'neutral'}
          icon={null}
          label={(goal?.options?.live) !== false ? 'Live data' : 'Historical'}
          tooltip={(goal?.options?.live) !== false
            ? 'Data streams in continuously — live monitoring and auto-retrain are enabled'
            : 'Fixed historical window — no new data arrives'}
        />
        {goal.shock_state === 'shock' && (
          <Tooltip
            title={(() => {
              const base = `Shock mode — a high-importance ${goal.shock_stream || 'event'} hit this goal. Forecasts serve with elevated uncertainty until the response fades.`;
              const b = goal.shock_blend;
              // A fitted blend = the platform LEARNED this mix from realized
              // shock outcomes — surface it instead of the default wording.
              return (b && Number.isFinite(Number(b.champion)))
                ? `${base} Blend fitted from past shocks: champion ${Math.round(Number(b.champion) * 100)}% · zero-shot ${Math.round(Number(b.zero_shot) * 100)}% · event overlay ${Math.round(Number(b.overlay) * 100)}%.`
                : base;
            })()}
            arrow
          >
            <Chip size="small" color="warning" icon={<BoltIcon />} label="Shock" />
          </Tooltip>
        )}
        {goal.shock_state === 'recovery' && (
          <Tooltip title="Recovering — the shock response is fading; serving anneals back to normal" arrow>
            <Chip size="small" variant="outlined" color="warning" icon={<BoltIcon />} label="Recovering" />
          </Tooltip>
        )}
      </Stack>
    );
  }, [goal]);

  const goalMeta = useMemo(() => {
    if (!goal) return null;
    const h = goal.horizon_bins;
    const parts = [GOAL_TYPE_LABELS[goal.goal_type] || goal.goal_type];
    if (goal.target_key) parts.push(`target: ${nameOf(goal.target_key)}`);
    if (typeof h === 'number') parts.push(`${h} step${h === 1 ? '' : 's'} ahead`);
    return parts.filter(Boolean).join('  ·  ');
  }, [goal, nameOf]);

  // First-build theater: while the goal's FIRST build runs, the page
  // auto-advances through the build's acts (driver funnel -> reveal ->
  // tournament -> forecast) with narration, so the user SEES what a goal
  // buys. Once per goal; any manual section click hands the page back.
  const { theaterCard, theaterHighlight, theaterActive } = useBuildTheater({
    goalId, section, activity, goal, runDoc,
  });

  // First-goal guided tour: a coach card that follows the goal's live
  // lifecycle and walks the user through Drivers -> Models -> Forecast ->
  // Scenarios -> Alerts. Self-gating (once per browser, skippable, no-op
  // for fleet goals) - see GoalTourGuide.js. Suspended while the theater
  // owns the stage; resumes at the right stop when it hands back.
  const { tourCard, tourHighlight } = useGoalTour({
    goalId, section, activity, goal, suspended: theaterActive,
  });
  const coachCard = theaterCard || tourCard;
  const navHighlight = theaterActive ? theaterHighlight : tourHighlight;

  // Companion aggregate goals are an implementation detail of fleets —
  // bounce to the parent so they never look like a second user-facing goal.
  if (goal?.companion_of) {
    return <Navigate to={`/goals/${goal.companion_of}`} replace />;
  }

  // Fleet goals share the same section rail; FleetGoalDetailPage fills each
  // tab with the fleet equivalent. Hooks above still run every render.
  if (goal?.goal_kind === 'fleet') {
    return (
      <FleetGoalDetailPage
        goal={goal}
        activity={activity}
        deleteGoal={deleteGoal}
        deleteState={deleteState}
      />
    );
  }

  const renderSection = () => {
    switch (section) {
      case 'drivers':
        return (
          <Stack spacing={2}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 0.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {runDoc?.status === 'completed' && (
                    <>
                      <BlurOnIcon sx={{ fontSize: 18 }} />
                      <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
                        Signal Universe
                      </Typography>
                    </>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {goal?.published_segment_id ? (
                    <Tooltip title="This goal's drivers were published as a segment — open it in the segment studio" arrow>
                      <span>
                        <CMButtonOutlinedSmall
                          startIcon={<DatasetIcon />}
                          component={RouterLink}
                          to={`/segments/${goal.published_segment_id}`}
                        >
                          Open segment
                        </CMButtonOutlinedSmall>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={SETTLED_STATUSES.includes(goal?.status)
                        ? 'Copy the validated drivers, engineered views and event channels into a segment you own and can edit'
                        : 'Available once discovery completes'}
                      arrow
                    >
                      <span>
                        <CMButtonOutlinedSmall
                          startIcon={<DatasetIcon />}
                          disabled={runActive || publishState?.isLoading
                            || !SETTLED_STATUSES.includes(goal?.status)}
                          onClick={() => publish({ goalId })}
                        >
                          Create segment
                        </CMButtonOutlinedSmall>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={runActive ? 'A run is already in progress' : 'Run discovery again on fresh data'} arrow>
                    <span>
                      <CMButtonOutlinedSmall
                        startIcon={<ReplayIcon />}
                        disabled={runActive || rerunState?.isLoading}
                        onClick={() => rerun({ goalId })}
                      >
                        Re-run
                      </CMButtonOutlinedSmall>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {runDoc?.status === 'completed' && <Divider />}
            </Box>
            {/* Relationships only make sense once discovery has finished —
                until then we just show the live run. */}
            {runDoc?.status === 'completed' && (
              <>
                <DriverRevealChart goalId={goalId} targetKey={goal?.target_key} />
                <ShockAnaloguesCard goal={goal} />
                <RelationshipGraph goalId={goalId} targetKey={goal?.target_key} />
                <PredictiveInfluencePanel goalId={goalId} />
                <PathwaysPanel goalId={goalId} goal={goal} />
              </>
            )}
            <DiscoveryRunCard run={runDoc} active={runActive} />
          </Stack>
        );

      case 'activity':
        return (
          <Stack spacing={2}>
            <AutopilotTimelineCard goalId={goalId} />
          </Stack>
        );

      case 'models':
        return (
          <Stack spacing={2}>
            <SectionLabel icon={<ModelTrainingIcon sx={{ fontSize: 18 }} />} text="Model fitting" />
            <TournamentBoard goalId={goalId} />
          </Stack>
        );

      case 'forecast':
        return <ForecastStudioPage embedded goalId={goalId} />;

      case 'scenarios':
        return <ScenariosPage embedded goalId={goalId} />;

      case 'serving':
        return (
          <GoalServing
            goalId={goalId}
            goal={goal}
            activity={activity}
            navigate={navigate}
          />
        );

      case 'alerts':
        return <GoalAlertsPanel goalId={goalId} goalType={goal?.goal_type} />;

      case 'settings':
        return (
          <Stack spacing={2}>
            <GoalSettingsCard goal={goal} embedded />
            <Box sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main', mb: 0.5 }}>
                Danger zone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Permanently delete this goal and everything it produced — drivers, the
                model tournament, forecasts and activity. This can&apos;t be undone.
              </Typography>
              <CMButtonError
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
                disabled={!goal || deleteState?.isLoading}
              >
                Delete goal
              </CMButtonError>
            </Box>
          </Stack>
        );

      case 'overview':
      default:
        return (
          <Grid container spacing={2}>
            {/* Dashboard — KPI tiles, serving */}
            <Grid item xs={12} lg={8}>
              <GoalDashboard
                goalId={goalId}
                goal={goal}
                activity={activity}
                totalCost={totalCost}
                navigate={navigate}
              />
            </Grid>
            {/* Activity — the lifecycle spine, vertical, beside the dashboard */}
            <Grid item xs={12} lg={4}>
              <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
                <GoalActivity
                  orientation="vertical"
                  goalId={goalId}
                  onOpenForecast={() => navigate(`/goals/${goalId}/forecast`)}
                  onGoToModels={() => navigate(`/goals/${goalId}/models`)}
                />
              </Stack>
            </Grid>
          </Grid>
        );
    }
  };

  if (!goal && !detailLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[
          { text: 'Goals', icon: <GoalsIcon />, link: '/goals' },
          { text: 'Goal' },
        ]} mb={2} />
        <Alert severity="warning">
          Goal not found — it may have been deleted.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs
        items={[
          { text: 'Goals', icon: <GoalsIcon />, link: '/goals' },
          section === 'overview'
            ? { text: goal?.name || '…' }
            : { text: goal?.name || '…', link: `/goals/${goalId}` },
          ...(section === 'overview' ? [] : [{ text: SECTION_LABELS[section] }]),
        ]}
        mb={0.5}
      />

      <Stack
        direction="row" spacing={1.5} alignItems="center"
        sx={{ mb: 2.5, mt: 0.5, flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 1 }}
      >
        {(() => {
          const TypeIcon = GOAL_TYPE_ICONS[goal?.goal_type] || GoalsIcon;
          return (
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}>
              <TypeIcon sx={{ fontSize: 24 }} />
            </Box>
          );
        })()}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {goal?.name || '…'}
          </Typography>
          {goalMeta && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
              {goalMeta}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{headerChips}</Box>
      </Stack>

      {rerunState?.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={rerunClear}>
          {String(rerunState.error)}
        </Alert>
      )}
      {publishState?.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={publishClear}>
          {String(publishState.error)}
        </Alert>
      )}
      {publishState?.response?.segment && publishState?.response?.created && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={publishClear}>
          Segment created —{' '}
          <Link component={RouterLink} to={`/segments/${publishState.response.segment.uid}`}>
            open it in the segment studio
          </Link>
          . It's yours to edit; the goal keeps its own internal copy.
        </Alert>
      )}
      {runDoc?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Last run failed{runDoc?.error ? ` — ${runDoc.error}` : ''}.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        <GoalSideNav goalId={goalId} active={section} highlight={navHighlight} />
        {coachCard}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {/* At-a-glance lives on Overview; deeper sections are focused. */}
          {section === 'overview' && (
            <GoalSummaryStrip phases={activity?.phases} />
          )}
          {renderSection()}
        </Paper>
      </Box>

      {/* Delete goal — destructive, so it's gated behind an explicit confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => { if (!deleteState?.isLoading) setDeleteOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this goal?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <strong>{goal?.name || 'This goal'}</strong> and everything it produced —
            discovered drivers, the model tournament, forecasts and activity — will be
            permanently deleted. This can&apos;t be undone.
          </Typography>
          {deleteState?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(deleteState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CMButtonOutlinedSmall
            onClick={() => setDeleteOpen(false)}
            disabled={deleteState?.isLoading}
          >
            Cancel
          </CMButtonOutlinedSmall>
          <CMButtonError
            startIcon={deleteState?.isLoading
              ? <CircularProgress size={14} color="inherit" />
              : <DeleteOutlineIcon />}
            onClick={() => deleteGoal({ goalId })}
            disabled={deleteState?.isLoading}
          >
            {deleteState?.isLoading ? 'Deleting…' : 'Delete goal'}
          </CMButtonError>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


const SectionLabel = ({ icon, text }) => (
  <Box sx={{ pt: 0.5 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
        {text}
      </Typography>
    </Stack>
    <Divider />
  </Box>
);

const mapStateToProps = (state) => ({
  detail: state.discovery_goal_detail?.response,
  detailLoading: Boolean(state.discovery_goal_detail?.isLoading),
  run: state.discovery_run?.response,
  rerunState: state.discovery_rerun_goal,
  publishState: state.discovery_publish_segment,
  deleteState: state.discovery_delete_goal,
  activity: state.discovery_goal_activity?.response,
  tournament: state.discovery_tournament?.response?.tournament,
});

const mapDispatchToProps = (dispatch) => ({
  fetchGoal: (payload) => dispatch(fetchDetail(payload)),
  clearGoal: () => dispatch(clearDetail()),
  runFetch: (payload) => dispatch(fetchRun(payload)),
  runClear: () => dispatch(clearRun()),
  rerun: (payload) => dispatch(rerunRequest(payload)),
  rerunClear: () => dispatch(clearRerun()),
  publish: (payload) => dispatch(publishRequest(payload)),
  publishClear: () => dispatch(clearPublish()),
  deleteGoal: (payload) => dispatch(deleteGoalRequest(payload)),
  deleteClear: () => dispatch(clearDeleteGoal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GoalDetailPage);
